/**
 * CoverageLockedRecipientBanner
 *
 * Displays an informational banner when a package recipient is locked to a coverage's carrier.
 * Fetches and displays coverage details to provide context about the locked relationship.
 */

import { InfoIcon } from 'lucide-react';
import React from 'react';

import { CoverageLink } from '@/components/entity-links/entities/CoverageLink';
import { useGetCoverageQuery } from '@/services/api/coverageService';

interface CoverageLockedRecipientBannerProps {
  matterId: string;
  coverageId: string;
  recipientDisplayName: string;
}

export const CoverageLockedRecipientBanner: React.FC<
  CoverageLockedRecipientBannerProps
> = ({ matterId, coverageId, recipientDisplayName }) => {
  const { data: coverage, isLoading } = useGetCoverageQuery({
    matterId,
    coverageId,
  });

  const policyNumber = coverage?.policy_identifiers?.[0]?.identifier_value;
  const category = coverage?.category;

  // Format category for display
  const categoryDisplay = (() => {
    switch (category?.toUpperCase()) {
      case 'FIRST_PARTY':
        return '1st Party';
      case 'THIRD_PARTY':
        return '3rd Party';
      case 'FIRST_PARTY_HEALTH':
        return '1st Party Health';
      default:
        return category || 'Coverage';
    }
  })();

  return (
    <div className="px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100">
      <div className="flex items-center gap-2">
        <InfoIcon className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
        {isLoading ? (
          <div className="h-3 w-32 bg-indigo-100 animate-pulse rounded" />
        ) : (
          <div className="flex items-center gap-1.5 text-[12px] text-indigo-900">
            <span>Locked to</span>
            <CoverageLink
              entityId={coverageId}
              matterId={matterId}
              className="font-medium hover:text-indigo-700 underline decoration-indigo-300 hover:decoration-indigo-500 transition-colors"
            >
              {recipientDisplayName}
            </CoverageLink>
            {categoryDisplay && (
              <span className="text-indigo-600">({categoryDisplay})</span>
            )}
            {policyNumber && (
              <>
                <span className="text-indigo-400">•</span>
                <span className="text-indigo-600">{policyNumber}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
